import type { QualifyingStatusProduct } from '@iheartjane/data-access';
import {
  Box,
  Button,
  CheckIcon,
  DismissIcon,
  Flex,
  Image,
  JaneGoldIcon,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { useFetcher } from 'react-router';

import type { CartActionResult } from '@jane/bloom/actions/cart.server';
import { useCheckoutLink } from '@jane/bloom/hooks/useCheckoutLink';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useBrandSpecialShortfallNotice } from '@jane/bloom/providers/brandSpecialShortfallProvider';
import { CartAction } from '@jane/bloom/routes/api.cart-items/types';
import type { BrandSpecialViewModel } from '@jane/bloom/viewModels/brandSpecialViewModel';

import styles from './goldBundleProgress.module.css';

export const GoldBundleProgress = ({
  brandSpecial,
  tagLabel,
}: {
  brandSpecial: BrandSpecialViewModel;
  tagLabel: string;
}) => {
  const { promoCode } = useRootLoaderData();
  const checkoutUrl = useCheckoutLink({ promoCode });

  return (
    <Flex flexDirection="column" className={styles['goldProgressContainer']}>
      <Flex
        flexDirection="column"
        px={24}
        py={brandSpecial?.allQualifiedProducts?.length > 0 ? 16 : 12}
      >
        {brandSpecial?.allQualifiedProducts?.length > 0 &&
          brandSpecial.allQualifiedProducts.map((product, index) => (
            <GoldBundleProduct
              key={`${product.id}-${index}`}
              product={product}
              tagLabel={tagLabel}
            />
          ))}
      </Flex>
      <Flex flexDirection="column">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px={24}
          className={
            brandSpecial.isUnlocked ? styles['unlockedContainer'] : undefined
          }
        >
          <Flex alignItems="center">
            <Box background="gold-light" borderRadius="xs" p={12}>
              <JaneGoldIcon />
            </Box>
            <Typography variant="body-semibold" ml={16}>
              {brandSpecial?.qualifyingStatusMsg}
            </Typography>
          </Flex>

          {brandSpecial.isUnlocked && (
            <Flex>
              <CheckIcon />
            </Flex>
          )}
        </Flex>

        {brandSpecial.isUnlocked && (
          <Flex mt={16} mx={24}>
            <Button label="Checkout now" full to={checkoutUrl} />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const GoldBundleProduct = ({
  product,
  tagLabel,
}: {
  product: QualifyingStatusProduct;
  tagLabel: string;
}) => {
  const fetcher = useFetcher<CartActionResult>();
  useBrandSpecialShortfallNotice(fetcher.data);

  const handleRemove = () => {
    fetcher.submit(
      {
        intent: CartAction.DeleteItem,
        priceId: product.price_id ?? null,
        productId: product.id?.toString() ?? null,
      },
      {
        action: `/api/cart-items`,
        method: 'POST',
      }
    );
  };

  return (
    <div className={styles['productRow']}>
      <div className={styles['productInfo']}>
        <Box position="relative">
          <Image
            src={product.image_urls?.[0] || ''}
            altText={product.name || 'Product image'}
            width="48px"
            height="48px"
            className={styles['productImage']}
          />
          <Flex
            top={-8}
            right={-8}
            width={24}
            height={24}
            position="absolute"
            background="grays-white"
            border="grays-light"
            borderRadius="lg"
            alignItems="center"
            justifyContent="center"
          >
            <button
              type="button"
              className={styles['removeButton']}
              onClick={handleRemove}
              disabled={fetcher.state === 'submitting'}
            >
              <DismissIcon className={styles['icon__xs']} />
            </button>
          </Flex>
        </Box>
        <Typography className={styles['productName']} truncateAt="100%">
          {product.name}
        </Typography>
      </div>
      <div className={styles['tagContainer']}>
        <Tag
          label={tagLabel}
          icon={<JaneGoldIcon />}
          background="gold"
          color="grays-black"
          size="sm"
        />
      </div>
    </div>
  );
};
